import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The transient status line (#status).
 *
 * Ported behaviour: every message shows, then fades after STATUS_HIDE_MS — nothing stays
 * parked there permanently. The strip overlaps the terminal's bottom rows, so a message
 * left up is covering live output.
 *
 * Re-setting the same text still re-shows and restarts the timer, which matters for
 * repeated events (a second `cd`, another failed send) — otherwise the user gets no
 * feedback the second time.
 */
const STATUS_HIDE_MS = 3000

export function useStatus() {
  const [text, setText] = useState<string | null>(null)
  const [shown, setShown] = useState(false)
  const timer = useRef<number | null>(null)
  // Bumped on every set so identical text still re-triggers the effect.
  const nonce = useRef(0)
  const [, force] = useState(0)

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
  }, [])

  const status = useCallback((msg: string) => {
    setText(msg)
    setShown(true)
    nonce.current += 1
    force(nonce.current)
  }, [])

  useEffect(() => {
    if (!shown) return
    clear()
    timer.current = window.setTimeout(() => {
      timer.current = null
      setShown(false)
    }, STATUS_HIDE_MS)
    return clear
    // The forced re-render above makes a repeat of the same message restart this.
  }, [shown, text, clear])

  useEffect(() => clear, [clear])

  return { text, shown, status }
}
